import PropTypes from 'prop-types';

import {Box, Flex, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';

/**
 * AwardBlock component – 50/50 layout (same styling as Section Panel variant 50/50).
 * Renders leftContentCollection in the left region and rightContentCollection in the right region.
 *
 * @param {Object} props - Component props
 * @param {Contentful.AwardBlock} props.component - AwardBlock component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
export const AwardBlock = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/award-block', {
    component,
    isComplete,
    isFetching,
  });

  const isValidItem = (item) =>
    item != null && typeof item === 'object' && item.__typename && item.sys?.id;

  const leftValid = (component?.leftContentCollection?.items ?? []).filter(isValidItem);
  const rightValid = (component?.rightContentCollection?.items ?? []).filter(isValidItem);

  if (leftValid.length === 0 && rightValid.length === 0) {
    return null;
  }

  return (
    <Box className="component component--award-block" {...styles.root} {...props}>
      <Flex {...styles.container}>
        <Box {...styles.leftRegion}>
          {leftValid.map((item) => (
            <ContentfulComponent
              key={item.sys.id}
              component={item}
              isComplete={isComplete}
              isFetching={isFetching}
            />
          ))}
        </Box>
        <Box {...styles.rightRegion}>
          {rightValid.map((item) => (
            <ContentfulComponent
              key={item.sys.id}
              component={item}
              isComplete={isComplete}
              isFetching={isFetching}
            />
          ))}
        </Box>
      </Flex>
    </Box>
  );
};

AwardBlock.displayName = 'AwardBlock';
AwardBlock.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
